/**
 * <rich-text-collapsible> — "Collapse text" option of the rich-text section.
 *
 * Counts sentences across the section's text blocks (in order) and hides
 * everything after the Nth one; the toggle button reveals / hides the rest.
 * The DOM is not rebuilt: the paragraph holding the cut keeps its tail in a
 * hidden inline <span>, so expanding reflows the same paragraph instead of
 * breaking it in two. Later paragraphs and later text blocks get hidden whole.
 *
 * Without JS (or when the text is short enough) the full text shows and the
 * toggle stays hidden.
 */
if (!customElements.get('rich-text-collapsible')) {
  customElements.define(
    'rich-text-collapsible',
    class RichTextCollapsible extends HTMLElement {
      // A run of . ! ? … (plus closing quotes/brackets) followed by whitespace and
      // a character that is not a lowercase letter, or by the end of the node.
      // The lowercase check keeps "т.е. някой" / "e.g. this" from counting.
      static SENTENCE_END = /[.!?…]+["'”»)\]]*(?=\s+[^\s\p{Ll}]|\s*$)/gu;

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.toggleButton = this.querySelector('.rich-text__toggle');
        if (!this.toggleButton) return;
        this.toggleLabel = this.toggleButton.querySelector('.rich-text__toggle-label') || this.toggleButton;

        const limit = parseInt(this.dataset.sentences, 10);
        if (!limit || !this.collapse(limit)) return;

        this.toggleButton.hidden = false;
        this.toggleButton.addEventListener('click', () => this.setExpanded(this.hasAttribute('data-collapsed')));
        this.setExpanded(false);

        // Theme editor: selecting a text block opens the text so it can be seen.
        this.addEventListener('shopify:block:select', (event) => {
          if (event.target.classList?.contains('rich-text__text')) this.setExpanded(true);
        });
      }

      setExpanded(expanded) {
        const wasExpanded = !this.hasAttribute('data-collapsed');
        this.toggleAttribute('data-collapsed', !expanded);
        this.toggleButton.setAttribute('aria-expanded', String(expanded));
        this.toggleLabel.textContent = expanded
          ? this.toggleButton.dataset.labelLess
          : this.toggleButton.dataset.labelMore;

        // Collapsing a long text can leave the reader below the section.
        if (wasExpanded && !expanded && this.getBoundingClientRect().top < 0) {
          const reduced = window.Theme?.config?.motionReduced;
          this.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
        }
      }

      /** Hides everything after `limit` sentences. Returns false when nothing is left to hide. */
      collapse(limit) {
        const blocks = Array.from(this.querySelectorAll('.rich-text__text'));
        const roots = blocks.map((block) => block.querySelector('.rich-text__text-body') || block);

        const cut = this.findCut(roots, limit);
        if (!cut || !this.hasTextAfter(cut, roots)) return false;

        this.hideAfter(cut.node, cut.offset, cut.root);
        this.markCutElement(cut.node, cut.root);
        blocks.slice(cut.index + 1).forEach((block) => block.classList.add('rich-text__more'));
        return true;
      }

      findCut(roots, limit) {
        let count = 0;
        for (const [index, root] of roots.entries()) {
          const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
          let node;
          while ((node = walker.nextNode())) {
            for (const match of node.data.matchAll(RichTextCollapsible.SENTENCE_END)) {
              count += 1;
              if (count === limit) return { index, root, node, offset: match.index + match[0].length };
            }
          }
        }
        return null;
      }

      hasTextAfter(cut, roots) {
        const range = document.createRange();
        range.setStart(cut.node, cut.offset);
        range.setEnd(cut.root, cut.root.childNodes.length);
        if (range.toString().trim() !== '') return true;
        return roots.slice(cut.index + 1).some((root) => root.textContent.trim() !== '');
      }

      hideAfter(node, offset, root) {
        let current = offset < node.length ? node.splitText(offset) : this.nextInTree(node, root);
        while (current) {
          const parent = current.parentNode;
          for (let sibling = current; sibling; ) {
            const next = sibling.nextSibling;
            this.hideNode(sibling);
            sibling = next;
          }
          current = parent === root ? null : this.nextInTree(parent, root);
        }
      }

      nextInTree(node, root) {
        while (node && node !== root) {
          if (node.nextSibling) return node.nextSibling;
          node = node.parentNode;
        }
        return null;
      }

      hideNode(node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          node.classList.add('rich-text__more');
        } else if (node.nodeType === Node.TEXT_NODE) {
          const wrapper = document.createElement('span');
          wrapper.className = 'rich-text__more';
          node.replaceWith(wrapper);
          wrapper.append(node);
        }
      }

      /** Flags the top-level element holding the cut, so its bottom margin can go while collapsed. */
      markCutElement(node, root) {
        while (node.parentNode && node.parentNode !== root) node = node.parentNode;
        if (node.nodeType === Node.ELEMENT_NODE) node.classList.add('rich-text__cut');
      }
    }
  );
}
